import { useState } from "react";
import { useAuth } from "../lib/auth";

const HERO_URL =
  "https://images.unsplash.com/photo-1543353071-10c8ba85a904?w=1800&auto=format&fit=crop&q=80";

// Soft shadow so light hero text stays legible over brighter patches of the photo.
const TEXT_SHADOW = "0 1px 16px rgba(13,12,10,0.6)";

function isMobileBrowser() {
  if (typeof window === "undefined") return false;
  if ((window as any).Capacitor?.isNativePlatform?.()) return false;
  const ua = navigator.userAgent || "";
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
}

export function Onboarding() {
  const { signInGoogle, signInApple } = useAuth();
  const [busy, setBusy] = useState<null | "google" | "apple">(null);
  const [error, setError] = useState<string | null>(null);
  const mobile = isMobileBrowser();

  const handle = async (which: "google" | "apple") => {
    setError(null);
    setBusy(which);
    try {
      if (which === "google") await signInGoogle();
      else await signInApple();
    } catch (e: any) {
      setError(e?.message ?? "Sign-in failed.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fade-in" style={{ position: "relative", minHeight: "calc(100vh / var(--app-zoom, 1))", overflow: "hidden" }}>
      <div
        className="food-img"
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url("${HERO_URL}")`,
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          // Left scrim darkens the text column (fading out by ~68% so the food
          // and sign-in card stay vivid); vertical scrim anchors the bottom.
          background:
            "linear-gradient(90deg, rgba(13,12,10,0.85) 0%, rgba(13,12,10,0.58) 30%, rgba(13,12,10,0.12) 54%, rgba(13,12,10,0) 68%)," +
            "linear-gradient(180deg, rgba(13,12,10,0.32) 0%, rgba(13,12,10,0.46) 50%, rgba(13,12,10,0.92) 100%)",
        }}
      />
      <div
        style={{
          position: "relative",
          minHeight: "calc(100vh / var(--app-zoom, 1))",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            // Keep the masthead clear of the iOS status bar / Dynamic Island.
            padding: "calc(32px + env(safe-area-inset-top)) 56px 32px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-serif)",
              fontWeight: 700,
              fontSize: 28,
              color: "#f0eee9",
              textShadow: TEXT_SHADOW,
            }}
          >
            Affinage
          </span>
          <span className="eb-mono" style={{ color: "rgba(240,238,233,0.7)", textShadow: TEXT_SHADOW }}>VOL. I · NO. 1</span>
        </div>
        <div style={{ flex: 1 }} />
        <div
          className="onboarding-grid"
          style={{
            padding: "0 80px calc(88px + env(safe-area-inset-bottom))",
            display: "grid",
            gridTemplateColumns: "1.35fr 1fr",
            gap: 80,
            alignItems: "end",
          }}
        >
          <div>
            <div className="eb" style={{ marginBottom: 22, color: "rgba(240,238,233,0.72)", textShadow: TEXT_SHADOW }}>
              THE SLOW ART OF REFINING A RECIPE
            </div>
            <div
              className="h1"
              style={{
                fontSize: "clamp(40px, 6vw, 72px)",
                lineHeight: 0.95,
                maxWidth: 760,
                color: "#f0eee9",
                textShadow: TEXT_SHADOW,
              }}
            >
              A place for the recipes{" "}
              <span style={{ fontStyle: "italic", fontWeight: 400, color: "var(--accent-bright)" }}>
                you actually cook
              </span>
              ,
            </div>
            <div
              className="h1"
              style={{
                fontSize: "clamp(40px, 6vw, 72px)",
                lineHeight: 0.95,
                fontWeight: 400,
                fontStyle: "italic",
                color: "rgba(240,238,233,0.82)",
                textShadow: TEXT_SHADOW,
              }}
            >
              refined over time.
            </div>
            <div
              style={{
                marginTop: 24,
                color: "rgba(240,238,233,0.85)",
                fontFamily: "var(--font-serif)",
                fontStyle: "italic",
                fontSize: 18,
                lineHeight: 1.55,
                maxWidth: 520,
                textShadow: TEXT_SHADOW,
              }}
            >
              An <em>affineur</em> tends a wheel of cheese for months — turning, judging,
              adjusting — never finished, only further along. A cook does the same with a
              recipe. Every cook a small refinement, every edit kept. Recipes here are
              something you tend, not store.
            </div>
          </div>
          <div>
            <div
              style={{
                background: "rgba(22,20,15,0.94)",
                border: "1px solid var(--line)",
                padding: 32,
              }}
            >
              <div className="eb" style={{ marginBottom: 14 }}>
                {mobile ? "BEST ON DESKTOP" : "BEGIN"}
              </div>
              {mobile ? (
                <>
                  <div
                    style={{
                      fontFamily: "var(--font-serif)",
                      fontSize: 16,
                      lineHeight: 1.55,
                      color: "var(--fg)",
                      marginBottom: 14,
                    }}
                  >
                    Affinage is built for a wider screen. Please open this page on a desktop
                    browser to start tending your cookbook.
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--font-serif)",
                      fontStyle: "italic",
                      fontSize: 13,
                      color: "var(--fg-muted)",
                      lineHeight: 1.6,
                    }}
                  >
                    A mobile experience is on the way.
                  </div>
                </>
              ) : (
                <>
                  <button
                    onClick={() => handle("google")}
                    disabled={busy != null}
                    className="btn large white block"
                    style={{ marginBottom: 10 }}
                  >
                    {busy === "google" ? <span className="spinner" /> : "Continue with Google"}
                  </button>
                  <button
                    onClick={() => handle("apple")}
                    disabled={busy != null}
                    className="btn large white block"
                  >
                    {busy === "apple" ? <span className="spinner" /> : "Continue with Apple"}
                  </button>
                  {error && <div className="banner error">{error}</div>}
                  <div
                    style={{
                      marginTop: 20,
                      fontFamily: "var(--font-serif)",
                      fontStyle: "italic",
                      fontSize: 12,
                      color: "var(--fg-muted)",
                      lineHeight: 1.6,
                    }}
                  >
                    Free to keep your own cookbook. We don't sell newsletters, ranked feeds, or the
                    recipes you store.
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 900px) {
          .onboarding-grid { grid-template-columns: 1fr !important; padding: 0 24px 56px !important; gap: 32px !important; }
        }
      `}</style>
    </div>
  );
}
